import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import React from 'react';
import { StatusBar } from 'expo-status-bar';
import { LogBox } from 'react-native';
import { DripsyProvider } from 'dripsy';
import { FontDisplay, useFonts } from 'expo-font';
import pWaitFor from 'p-wait-for';
import * as SplashScreen from 'expo-splash-screen';
import * as SystemUI from 'expo-system-ui';
import { ModalProvider } from 'react-native-modalfy';
import { PortalHost, PortalProvider } from '@gorhom/portal';
import { Audio } from 'expo-av';

import { theme, globalStyles } from '@styles';
import {
  TokenService,
  NetInfoService,
  AppStateService,
  LocalizationService,
  PersistService,
  CrashlyticsService,
  ToastService,
  NotificationsService,
  TagManagerService,
  AnalyticsService,
  SecondaryAnalyticsService,
  BrazeService,
} from '@services';
import { useAsyncInit, useInAppPurchase } from '@hooks';
import { ToastRoot } from '@components';
import { createRootModel } from '@models/createRootModel';
import { RootModelProvider } from '@models/useRoot';
import {
  AppNavigator,
  navigationRef,
} from '@navigation/AppNavigator';
import { modalStack } from '@modals/ModalStack';
import { IS_WEB, parseApiError } from '@utils';
import {
  DeepLinkingProvider,
  EventEmitterProvider,
  StripeElementsProvider,
} from '@context';
import { ApiEvents } from '@api';

// temporaly fix for issue for react-native-reanimated with expo
// https://github.com/expo/expo/issues/22333#issuecomment-1530909698
if (IS_WEB) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (window._frameTimestamp === undefined) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window._frameTimestamp = null;
  }
}

LogBox.ignoreLogs([
  // Some libraries are using old API
  // eslint-disable-next-line max-len
  "[react-native-gesture-handler] Seems like you're using an old API with gesture components, check out new Gestures system!",
  // State serialization is only helpful for cases when you are using a navigation state persistence,
  // but we are not using it
  'Non-serializable values were found in the navigation state',
]);

CrashlyticsService.init();

const rootModel = createRootModel();

// uncomment this to enable reactotron
// do not push this!
// if (__DEV__) {
//   import('./devtools/reactotron').then(() => {
//     require('./devtools/reactotron').reactotronTrack(rootModel.store);
//   });
// }

NotificationsService.setBackgroundMessageHandler(() =>
  Promise.resolve(),
);

function App() {
  const [fontsLoaded] = useFonts({
    LatoRegular: {
      uri: require('./assets/fonts/Lato-Regular.ttf'),
      display: FontDisplay.SWAP,
    },
    LatoSemiBold: {
      uri: require('./assets/fonts/Lato-SemiBold.ttf'),
      display: FontDisplay.SWAP,
    },
    IcoMoon: {
      uri: require('./assets/icomoon/icomoon.ttf'),
      display: FontDisplay.SWAP,
    },
  });

  // Init in-app-purchase services and add updated purchase listener (listener is optional)
  useInAppPurchase();

  // Start listen to logout event
  useAsyncInit(async () => {
    ApiEvents.addLogoutListener(() => {
      rootModel.store.auth.signOut.run();
      rootModel.store.auth.unauthorize();
    });
  });

  // Initialization flow
  useAsyncInit(async () => {
    // For iOS, ignore silent mode to avoid muted videos and tracks
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Audio.setAudioModeAsync({ playsInSilentModeIOS: true });

    SystemUI.setBackgroundColorAsync('white');

    await SplashScreen.preventAutoHideAsync();

    // creates persist and save it in service in order to have an access to
    // persist's instance from everywhere
    PersistService.persist = await rootModel.createPersist();

    // use this to remove persisted data
    // await PersistService.persist.purge();

    // restores data from local storage
    // await PersistService.persist.rehydrate();

    // restores data from local storage or remove it if could not restore
    await PersistService.rehydrateSafe();

    // Init services and load fonts
    BrazeService.init();
    AppStateService.init();
    TagManagerService.init();
    AnalyticsService.init();
    SecondaryAnalyticsService.init();
    await Promise.all([
      NetInfoService.init(),
      LocalizationService.init(),
      pWaitFor(() => !fontsLoaded),
    ]);

    const tokens = await TokenService.get();

    if (tokens) {
      rootModel.store.viewer.fetch
        .run()
        ?.then(() => {}) // Do nothing
        .catch((err) => {
          const error = parseApiError(err);

          ToastService.showError(error.message);
        });

      rootModel.store.auth.authorize();
    } else {
      rootModel.store.auth.unauthorize();
    }

    await pWaitFor(() => navigationRef.isReady());
    await SplashScreen.hideAsync();
  });

  if (!fontsLoaded) {
    return null;
  }

  return (
    <GestureHandlerRootView style={globalStyles.wrapper}>
      <RootModelProvider value={rootModel.store}>
        <EventEmitterProvider>
          <DripsyProvider theme={theme}>
            <SafeAreaProvider>
              <PortalProvider shouldAddRootHost={false}>
                <ModalProvider stack={modalStack}>
                  <StatusBar style="dark" />
                  <DeepLinkingProvider>
                    <StripeElementsProvider>
                      <AppNavigator />
                    </StripeElementsProvider>
                  </DeepLinkingProvider>

                  {/**
                   * PortalProvider cannot be inside ModalProvider
                   * as portal could lose the context, so we using here PortalHost instead
                   */}
                  <PortalHost name="root" />
                </ModalProvider>
              </PortalProvider>
              <ToastRoot />
            </SafeAreaProvider>
          </DripsyProvider>
        </EventEmitterProvider>
      </RootModelProvider>
    </GestureHandlerRootView>
  );
}

export default CrashlyticsService.wrap(App);
